import { network } from "../config";
import {
  PayoneerPayoutInfo,
  PayoneerPayRequest,
  PayRequestsStatuses,
} from "../types/PayoutTypes";
import TypeDefaultResponse from "../types/TypeDefaultResponse";
import url from "../utils/formApiUrl";
import customKy from "./kyExtend";

const { payoneer } = network;

const baseURL = process.env.REACT_APP_HTTP_CONNECTION_STRING + "payout/";

const getAuthHeader = (token: string) => {
  return { Authorization: `Bearer ${token}` };
};

export default class PayoneerApi {
  static async savePayoneerPayoutInfo(
    token: string,
    newPayoutInfo: Omit<PayoneerPayoutInfo, "user" | "_id">,
  ): Promise<
    TypeDefaultResponse & {
      payoutInfo?: PayoneerPayoutInfo;
    }
  > {
    return await customKy
      .put(url(baseURL, payoneer.savePayoneerPayoutInfo), {
        headers: getAuthHeader(token),
        json: { newPayoutInfo },
      })
      .then((res) => res.json());
  }

  static async getPayoneerPayoutInfo(token: string): Promise<
    TypeDefaultResponse & {
      payoutInfo?: PayoneerPayoutInfo | null;
    }
  > {
    return await customKy
      .get(url(baseURL, payoneer.getPayoneerPayoutInfo), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async createPayoneerPayRequest(
    token: string,
    amount: number,
  ): Promise<
    TypeDefaultResponse & {
      request?: PayoneerPayRequest | null;
    }
  > {
    return await customKy
      .post(url(baseURL, payoneer.createPayoneerPayRequest), {
        headers: getAuthHeader(token),
        json: { amount },
      })
      .then((res) => res.json());
  }

  static async getPayoneerPayRequests(
    token: string,
    page: number,
    limit: number,
    status?: PayRequestsStatuses,
  ): Promise<
    TypeDefaultResponse & {
      requests?: PayoneerPayRequest[];
      totalAmount: number;
    }
  > {
    const params = new URLSearchParams();
    if (status) {
      params.append("status", status);
    }
    return await customKy
      .get(
        url(baseURL, payoneer.getPayoneerPayRequests, { page, limit }, params),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }

  static async getMyPayoneerPayRequests(
    token: string,
    status?: PayRequestsStatuses,
  ): Promise<
    TypeDefaultResponse & {
      requests?: PayoneerPayRequest[];
    }
  > {
    const formaData = new FormData();
    if (status) {
      formaData.append("status", status);
    }
    return await customKy
      .get(url(baseURL, payoneer.getMyPayoneerPayRequests), {
        headers: getAuthHeader(token),
        body: formaData,
      })
      .then((res) => res.json());
  }

  static async cancelPayoneerPayRequest(
    token: string,
    requestId: string,
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(url(baseURL, payoneer.cancelPayoneerPayRequest), {
        headers: getAuthHeader(token),
        json: { requestId },
      })
      .then((res) => res.json());
  }

  static async changePayoneerPayRequestStatus(
    token: string,
    payload: {
      requestId: string;
      status: PayRequestsStatuses;
      cancelationReason?: string;
    },
  ): Promise<
    TypeDefaultResponse & {
      request?: PayoneerPayRequest | null;
    }
  > {
    return await customKy
      .patch(url(baseURL, payoneer.changePayoneerPayRequestStatus), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }
}
